import React from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TimeConverter } from "../../services/utils/TimeConverter";
import { getMedleyLegsByDistance, getShortStrokeName } from "../../services/utils/EventOrdering";
const timeConverter = new TimeConverter();
// Labelling every tick with its stroke (rather than only the first of each leg)
// keeps the point-to-stroke mapping unambiguous when a leg spans several lengths.
const SplitAxisTick = ({ x = 0, y = 0, payload, legs }) => {
    const leg = payload ? legs?.get(payload.value) : undefined;
    return (React.createElement("g", { transform: `translate(${x},${y})` },
        React.createElement("text", { dy: 12, textAnchor: "middle", fill: "var(--gray-400)", fontSize: 11 },
            payload?.value,
            "m"),
        leg && (React.createElement("text", { dy: 25, textAnchor: "middle", fill: "var(--gray-500)", fontSize: 10, fontWeight: 600 }, getShortStrokeName(leg)))));
};
const renderTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0)
        return null;
    const point = payload[0].payload;
    return (React.createElement("div", { style: {
            background: 'var(--card-bg)',
            border: '1px solid var(--gray-100)',
            borderRadius: '4px',
            padding: '0.4rem 0.6rem',
            fontSize: '0.8rem',
        } },
        React.createElement("div", null,
            React.createElement("strong", null, point.lapLabel),
            " \u00B7 ",
            point.distance,
            "m split"),
        point.leg && React.createElement("div", { style: { color: 'var(--gray-500)' } }, point.leg),
        React.createElement("div", { style: { color: 'var(--gray-400)' } },
            point.cumulativeLabel,
            " cumulative")));
};
const SplitsTrendChart = ({ splits, event }) => {
    // Derive each lap from the cumulative deltas so the per-lap values always sum
    // back to the finishing time. The source omits the incremental on the first
    // length (it equals the first cumulative), so cumulative deltas are the robust
    // basis for all points, including the first.
    let previousCumulative = 0;
    const parsed = splits
        .map(split => ({
        distance: Number(split.distance),
        cumulative: timeConverter.parseTimeToSeconds(split.cumulative),
    }))
        .filter(p => isFinite(p.distance) && p.distance > 0 && isFinite(p.cumulative))
        .sort((a, b) => a.distance - b.distance);
    // Empty for everything but a medley whose splits line up with the four legs.
    const legs = event ? getMedleyLegsByDistance(event, parsed.map(p => p.distance)) : new Map();
    const points = parsed.map(p => {
        const lap = p.cumulative - previousCumulative;
        previousCumulative = p.cumulative;
        return {
            distance: p.distance,
            lap,
            lapLabel: timeConverter.formatSecondsToTime(lap),
            cumulative: p.cumulative,
            cumulativeLabel: timeConverter.formatSecondsToTime(p.cumulative),
            leg: legs.get(p.distance),
        };
    });
    if (points.length < 2) {
        return null;
    }
    const laps = points.map(p => p.lap);
    const minLap = Math.min(...laps);
    const maxLap = Math.max(...laps);
    // Fixed buffer rather than a % of range, so a tight spread still gets visible
    // headroom instead of being squashed flat.
    const yBuffer = Math.max((maxLap - minLap) * 0.15, 0.3);
    return (React.createElement("div", { style: { width: "100%", height: legs.size > 0 ? 198 : 180, marginBottom: '0.75rem' } },
        React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
            React.createElement(LineChart, { data: points, margin: { top: 8, right: 12, bottom: 0, left: 0 } },
                React.createElement(CartesianGrid, { strokeDasharray: "3 3", stroke: "var(--gray-100)", vertical: false }),
                React.createElement(XAxis, { dataKey: "distance", type: "number", domain: ['dataMin', 'dataMax'], ticks: points.map(p => p.distance), height: legs.size > 0 ? 48 : 30, tick: React.createElement(SplitAxisTick, { legs: legs }), axisLine: { stroke: 'var(--gray-100)' }, tickLine: false }),
                React.createElement(YAxis, { width: 48, tick: { fontSize: 11, fill: 'var(--gray-400)' }, axisLine: false, tickLine: false, tickFormatter: (value) => timeConverter.formatSecondsToTime(value), domain: [minLap - yBuffer, maxLap + yBuffer] }),
                React.createElement(Tooltip, { content: renderTooltip }),
                React.createElement(Line, { type: "linear", dataKey: "lap", stroke: "var(--danger)", strokeWidth: 2, animationDuration: 1200, dot: { r: 3, stroke: 'var(--danger)', fill: 'var(--danger)' }, activeDot: { r: 5 } })))));
};
export default SplitsTrendChart;
